import { isIos } from '@remarkablenet/utils';

const DEFAULT_DEBOUNCE_TIMEOUT = 250;
let debounceTimeout: null | ReturnType<typeof setTimeout> = null;

export const addClass = (elm: HTMLElement, className: string[]) => elm.classList.add(...className);

export const convertObjectKeysToArray = (obj) => Object.keys(obj).map((data) => [data, obj[data]]);

export const convertObjectValuesToArray = (obj) => Object.values(obj).map((data: any) => [data, obj[data]]);

export const removeClass = (elm: HTMLElement, className: string[]) => elm.classList.remove(...className);

export const setStateClass = (elm, possibleStateClasses, stateClassToAdd) => {
  possibleStateClasses.forEach((stateClass) => removeClass(elm, stateClass));
  addClass(elm, stateClassToAdd);
};

export const toggleClass = (elm, className, shouldAddClass) => elm.classList.toggle(className, shouldAddClass);

export const setStylesOnElement = (styles, element) => Object.assign(element.style, styles);

export const insertAfter = (referenceNode, elmToAppend) =>
  referenceNode.parentNode.insertBefore(elmToAppend, referenceNode.nextSibling);

export const findElementPosition = (elm) => {
  const rect = elm.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  const coords = {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft
  };

  return coords;
};

export const queryStringFromObject = (params) =>
  Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

const format = (k, v) => (v !== null ? `${k}=${encodeURIComponent(v)}` : '');
export const queryStringFromObject2 = (obj) => {
  return []
    .concat(
      ...Object.entries(obj).map(([k, v]) =>
        Array.isArray(v) ? v.map((arr) => queryStringFromObject2({ [k]: arr })) : format(k, v)
      )
    )
    .filter((x) => x)
    .join('&');
};

export const waitForFrame = <T>(data?: T): Promise<T> =>
  new Promise((resolve) => requestAnimationFrame(() => resolve(data)));
export const checkForModuleToLoad = (elm) => elm.querySelector('.pageJS');
export const getModuleToLoad = (elm) => elm.querySelector('.pageJS').getAttribute('module');
export const getSrcToLoad = (elm) => elm.getAttribute('src');
export const bindModuleToLoad = (module) => window[module].init.bind(window)();
export const unbindModuleToLoade = (module) => window[module].exit.bind(window)();

export const appendScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    document.body.appendChild(script);
    script.onload = resolve;
    script.onerror = reject;
    script.async = true;
    script.src = src;
    script.classList.add('js-appended-script');
  });
};

export const removeAppendedScript = (selector = '.js-appended-script') => {
  const scripts = document.querySelectorAll(selector);
  scripts.forEach((script) => {
    script.remove();
  });
};

export const debounce = (func: Function, wait: number = DEFAULT_DEBOUNCE_TIMEOUT) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const findAncestor = (el, cls) => {
  while ((el = el.parentElement) && !el.classList.contains(cls));
  return el;
};

export const getParams = (query) => {
  if (!query) {
    return {};
  }
  return (/^[?#]/.test(query) ? query.slice(1) : query).split('&').reduce((params, param) => {
    let [key, value] = param.split('=');
    params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
    return params;
  }, {});
};

export const serialize = (obj) => {
  return (
    '?' +
    Object.entries(obj)
      .map(([key, val]) => `${key}=${val}`)
      .join('&')
  );
};

export const getOffset = (el) => {
  let _x = 0,
    _y = 0;
  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    _x += el.offsetLeft - el.scrollLeft;
    _y += el.offsetTop - el.scrollTop;
    el = el.offsetParent;
  }
  return { top: _y, left: _x } as ScrollToOptions;
};

export const scrollTo = (offset: ScrollToOptions, callback = null) => {
  scrollToOnContainer(offset, window, callback);
};

// do you need to scroll to an element that is with an overflow container?
// I.E a div with a scrollbar? or a mobile menu ?
// this function will help with that
// make sure you find the right container for your element
// use utility-functions.getOffset() to get the top and left of the element
export const scrollToOnContainer = (
  offset: ScrollToOptions,
  container: HTMLElement | Window = window,
  callback: Function = null
) => {
  var defaultOptions = {
    top: 0,
    left: 0,
    behavior: 'smooth'
  };
  const offsetOptions = Object.assign(defaultOptions, offset);
  const onScroll = function () {
    const scrollTop = window.scrollY || window.pageYOffset;
    if (scrollTop === offsetOptions.top) {
      container.removeEventListener('scroll', onScroll);
      if (callback) callback();
    }
  };
  container.addEventListener('scroll', onScroll);
  onScroll();
  container.scrollTo(offsetOptions);
};

export const getAbsoluteHeight = (element) => {
  const elm = typeof element === 'string' ? document.querySelector(element) : element;

  const styles = window.getComputedStyle(elm);
  const margin = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);

  return Math.ceil(elm.offsetHeight + margin);
};

export const getAntiForgeryToken = () => {
  const tokenElm: HTMLInputElement = document.querySelector('[name="__RequestVerificationToken"]');

  if (!tokenElm) return '';

  return tokenElm.value;
};

export const togglePasswordVisibility = () => {
  const elms: NodeListOf<HTMLElement> = document.querySelectorAll('.js-toggle-password');
  elms.forEach((elm) => {
    !elm.getAttribute('init') &&
      elm.addEventListener('click', (e) => {
        e.preventDefault();
        const shouldShow = elm.dataset.toggle === 'show';
        const input = elm.parentElement.querySelector('.js-toggle-password-field');
        const imgChild = elm.querySelector<HTMLImageElement>('img');
        input.setAttribute('type', shouldShow ? 'text' : 'password');
        elm.setAttribute('data-toggle', shouldShow ? 'hide' : 'show');

        if (!imgChild) {
          elm.innerHTML = shouldShow ? 'hide' : 'show';
          input.setAttribute('type', shouldShow ? 'text' : 'password');
        } else {
          const icon = elm.querySelector('img');
          icon.src = icon.getAttribute('data-src-' + (shouldShow ? 'hide' : 'show'));
        }
      });
    elm.setAttribute('init', 'true');
  });
};

export const dispatchCustomEventWrapper = (el: HTMLElement | Document, name: string, data?: LooseObject) => {
  const evt = new CustomEvent(name, {
    detail: data,
    bubbles: true
  });
  el.dispatchEvent(evt);
};

// function to disable scroll on the page
// use this function to prevent the page from scrolling when you have a modal open
// this is used on search modal open when its open in mobile view
export const disableScroll = () => {
  document.body.classList.add('disable-scroll');
  if (!isIos()) return;
  document.body.style.top = `-${window.scrollY}px`;
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';

  // Disable scrolling.
  document.ontouchmove = function (e) {
    e.preventDefault();
  };
};

// function to enable scroll on the page
// use this function to enable the page to scroll when you have a modal open
// this is used on search modal close when its open in mobile view
export const enableScroll = () => {
  document.body.classList.remove('disable-scroll');
  if (!isIos()) return;
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  window.scrollTo(0, parseInt(scrollY || '0') * -1);
  // Enable scrolling.
  document.ontouchmove = function (e) {
    return true;
  };
};

export const lighthouseImprovements = () => {
  /* begin lighthouse - add a "discernible name"/labels to links so we pass accessibility tests */
  /* replies on the img having a non empty & unique alt value and the link to have "homepage-promo" class added in cms */
  const homepageImgBlock = document.querySelectorAll('.homepage-promo') as NodeListOf<HTMLElement>;
  if (homepageImgBlock.length !== 0) {
    homepageImgBlock.forEach((item) => {
      const img = item.querySelector('img') as HTMLImageElement;
      img ? item.setAttribute('aria-label', img.alt) : null;
    });
  }

  const readMoreLink = document.getElementById('X1-link') as HTMLLinkElement;
  if (readMoreLink) {
    readMoreLink.setAttribute('aria-label', 'Read More');
    readMoreLink.href = '#';
    readMoreLink.addEventListener('click', (e) => {
      e.preventDefault;
    });
  }
  /* end lighthouse */
};


window.dataLayer = window.dataLayer || [];
export function gtag(event: any, action: any, params?: any): void {
  if (typeof action === 'string') {
    window.dataLayer.push(arguments);
  } else {
    window.dataLayer.push({ event, ...action });
  }
}

/**
 * Escapes HTML special characters in a string.
 *
 * This function creates a temporary `div` element, sets its `innerText` to the input string,
 * and then returns the `innerHTML` of the `div`, which will have the HTML special characters escaped.
 *
 * @param str - The input string that may contain HTML special characters.
 * @returns The escaped HTML string.
 */
export const escapeHTML = (str: string) => {
  const div = document.createElement('div');
  div.innerText = str;
  return div.innerHTML;
}

/**
 * This function checks if a given phone number is a valid UK mobile or landline number.
 * @param phone - phone number to check
 * @returns - true if valid UK mobile or landline number, false otherwise
 */
export const isValidPhoneNumberCheck = (phone: string) => {
    console.log('is valid number running ');
    const ukMobileRegex = /^(?:\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
    const ukLandlineRegex = /^(?:\+44\s?1\d{3}|\(?01\d{3}\)?|\+44\s?2\d{3}|\(?02\d{3}\)?|\+44\s?3\d{3}|\(?03\d{3}\)?)\s?\d{3}\s?\d{3,4}$/;
    const isUkMobile = ukMobileRegex.test(phone);
    const isUkLandline = ukLandlineRegex.test(phone);

    return isUkMobile || isUkLandline;
};